import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeRaw from 'rehype-raw';
import { BookOpen, ChevronRight, List, AlignLeft } from 'lucide-react';

const WebManualPage = () => {
    const [markdown, setMarkdown] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeId, setActiveId] = useState('');
    const [headings, setHeadings] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    useEffect(() => {
        const fetchManual = async () => {
            try {
                const response = await fetch('/user_manual_rendered_mermaid.md');
                if (!response.ok) {
                    throw new Error('Failed to fetch the manual.');
                }
                const text = await response.text();
                setMarkdown(text);
                setLoading(false);
            } catch (err) {
                console.error("Error loading manual:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchManual();
    }, []);

    useEffect(() => {
        if (loading || !markdown) return;
        
        const timeoutId = setTimeout(() => {
            const container = document.getElementById('markdown-content-container');
            if (!container) return;
            
            const headingElements = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
            const extractedHeadings = Array.from(headingElements).map((el) => {
                const level = parseInt(el.tagName.replace('H', ''), 10);
                return {
                    id: el.id,
                    text: el.innerText,
                    level: level,
                };
            }).filter(h => h.id);
            
            setHeadings(extractedHeadings);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [loading, markdown]);

    useEffect(() => {
        if (headings.length === 0) return;

        const handleScroll = () => {
            const headingElements = headings.map(h => document.getElementById(h.id)).filter(Boolean);
            
            let currentActiveId = '';
            for (const el of headingElements) {
                const rect = el.getBoundingClientRect();
                if (rect.top <= window.innerHeight / 2) {
                    currentActiveId = el.id;
                } else {
                    break;
                }
            }
            
            if (currentActiveId) {
                setActiveId(currentActiveId);
            }
        };

        const scrollContainer = document.getElementById('manual-scroll-container');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, [headings]);

    const handleTocClick = (e, id) => {
        e.preventDefault();
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveId(id);
            if (window.innerWidth < 768) {
                setSidebarOpen(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="premium-loader-container">
                <div className="premium-loader">
                    <div className="pulse-ring"></div>
                    <BookOpen size={40} className="pulse-icon text-indigo-600" />
                </div>
                <h3 className="mt-4 text-indigo-900 font-medium tracking-wide">Loading Documentation...</h3>
                <style>{`
                    .premium-loader-container {
                        height: 100vh;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                        font-family: 'Inter', system-ui, sans-serif;
                    }
                    .premium-loader {
                        position: relative;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 100px;
                        height: 100px;
                    }
                    .pulse-ring {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        border-radius: 50%;
                        background: rgba(99, 102, 241, 0.2);
                        animation: pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
                    }
                    .pulse-icon {
                        position: relative;
                        z-index: 10;
                        color: #4f46e5;
                        animation: pulse-icon 1.5s ease-in-out infinite alternate;
                    }
                    .text-indigo-900 { color: #312e81; }
                    .text-indigo-600 { color: #4f46e5; }
                    
                    @keyframes pulse-ring {
                        0% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.5); }
                        70% { transform: scale(1.3); box-shadow: 0 0 0 20px rgba(99, 102, 241, 0); }
                        100% { transform: scale(0.8); box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
                    }
                    @keyframes pulse-icon {
                        0% { transform: scale(0.9); }
                        100% { transform: scale(1.1); }
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
                <div className="alert alert-danger shadow-lg rounded-4 p-5 text-center" role="alert" style={{ maxWidth: '500px' }}>
                    <div className="mb-4 text-danger">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                    </div>
                    <h4 className="alert-heading fw-bold mb-3">Failed to Load</h4>
                    <p className="text-muted">{error}</p>
                    <button className="btn btn-outline-danger mt-3 rounded-pill px-4" onClick={() => window.location.reload()}>Try Again</button>
                </div>
            </div>
        );
    }

    return (
        <div className="premium-layout">
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Outfit:wght@400;500;600;700&family=Fira+Code:wght@400;500&display=swap" rel="stylesheet" />
            
            {/* Mobile Sidebar Toggle */}
            <button 
                className="btn btn-primary position-fixed rounded-circle shadow-lg d-md-none sidebar-toggle-btn"
                style={{ bottom: '20px', right: '20px', zIndex: 1050, width: '56px', height: '56px' }}
                onClick={() => setSidebarOpen(!sidebarOpen)}
            >
                {sidebarOpen ? <AlignLeft /> : <List />}
            </button>

            {/* Sidebar Table of Contents */}
            <div className={`premium-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <div className="d-flex align-items-center gap-2">
                        <div className="icon-box">
                            <BookOpen size={20} className="text-white" />
                        </div>
                        <h5 className="mb-0 fw-bold header-title">Documentation</h5>
                    </div>
                </div>
                <div className="sidebar-content">
                    <ul className="toc-list">
                        {headings.map((heading, index) => {
                            const indent = Math.max(0, heading.level - 1);
                            const isActive = activeId === heading.id;
                            
                            return (
                                <li key={`${heading.id}-${index}`} className="toc-item" style={{ paddingLeft: `${indent * 0.8}rem` }}>
                                    <a
                                        href={`#${heading.id}`}
                                        onClick={(e) => handleTocClick(e, heading.id)}
                                        className={`toc-link ${isActive ? 'active' : ''}`}
                                        title={heading.text}
                                    >
                                        {isActive && <ChevronRight size={14} className="active-icon" />}
                                        <span className="text-truncate">{heading.text}</span>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            {/* Main Content Area */}
            <div id="manual-scroll-container" className="premium-content-area">
                <div className="content-container animate-fade-in">
                    <div id="markdown-content-container" className="premium-markdown">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeSlug, rehypeRaw]}
                            components={{
                                code({ node, inline, className, children, ...props }) {
                                    return !inline ? (
                                        <div className="premium-code-block my-4">
                                            <div className="code-header">
                                                <div className="mac-dots">
                                                    <span className="dot red"></span>
                                                    <span className="dot yellow"></span>
                                                    <span className="dot green"></span>
                                                </div>
                                                <span className="code-lang">{className ? className.replace('language-', '') : 'code'}</span>
                                            </div>
                                            <div className="code-body">
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            </div>
                                        </div>
                                    ) : (
                                        <code className="inline-code" {...props}>
                                            {children}
                                        </code>
                                    );
                                },
                                img: ({node, ...props}) => (
                                    <div className="premium-image-container my-5">
                                        <img className="premium-image" {...props} />
                                    </div>
                                ),
                                table: ({node, ...props}) => (
                                    <div className="premium-table-container my-4">
                                        <table className="premium-table" {...props} />
                                    </div>
                                ),
                                th: ({node, ...props}) => <th {...props} />,
                                td: ({node, ...props}) => <td {...props} />,
                                h1: ({node, ...props}) => <h1 className="md-h1 mt-5 mb-4" {...props} />,
                                h2: ({node, ...props}) => <h2 className="md-h2 mt-5 mb-3" {...props} />,
                                h3: ({node, ...props}) => <h3 className="md-h3 mt-4 mb-3" {...props} />,
                                h4: ({node, ...props}) => <h4 className="md-h4 mt-4 mb-2" {...props} />,
                                p: ({node, ...props}) => <p className="md-p mb-4" {...props} />,
                                ul: ({node, ...props}) => <ul className="md-ul mb-4" {...props} />,
                                ol: ({node, ...props}) => <ol className="md-ol mb-4" {...props} />,
                                li: ({node, ...props}) => <li className="md-li mb-2" {...props} />,
                                a: ({node, ...props}) => <a className="md-link" {...props} />
                            }}
                        >
                            {markdown}
                        </ReactMarkdown>
                    </div>
                </div>
            </div>

            {/* Premium Styles */}
            <style>{`
                :root {
                    --primary: #4f46e5;
                    --primary-hover: #4338ca;
                    --bg-main: #f8fafc;
                    --bg-sidebar: rgba(255, 255, 255, 0.85);
                    --text-main: #1e293b;
                    --text-muted: #64748b;
                    --border-color: rgba(226, 232, 240, 0.8);
                }

                .premium-layout {
                    display: flex;
                    height: 100vh;
                    background-color: var(--bg-main);
                    background-image: 
                        radial-gradient(at 40% 20%, hsla(228,100%,74%,0.08) 0px, transparent 50%),
                        radial-gradient(at 80% 0%, hsla(189,100%,56%,0.08) 0px, transparent 50%),
                        radial-gradient(at 0% 50%, hsla(355,100%,93%,0.08) 0px, transparent 50%);
                    font-family: 'Inter', system-ui, -apple-system, sans-serif;
                    overflow: hidden;
                }

                /* Sidebar Styles */
                .premium-sidebar {
                    width: 320px;
                    min-width: 320px;
                    height: 100vh;
                    background: var(--bg-sidebar);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border-right: 1px solid var(--border-color);
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 1000;
                }

                .sidebar-header {
                    padding: 24px;
                    border-bottom: 1px solid var(--border-color);
                    background: linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%);
                }

                .icon-box {
                    background: linear-gradient(135deg, #6366f1, #a855f7);
                    padding: 8px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
                }

                .header-title {
                    font-family: 'Outfit', sans-serif;
                    color: #0f172a;
                    font-size: 1.25rem;
                }

                .sidebar-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                /* Custom Scrollbar for Sidebar */
                .sidebar-content::-webkit-scrollbar,
                #manual-scroll-container::-webkit-scrollbar,
                .code-body::-webkit-scrollbar,
                .premium-table-container::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }
                .sidebar-content::-webkit-scrollbar-track,
                #manual-scroll-container::-webkit-scrollbar-track,
                .code-body::-webkit-scrollbar-track,
                .premium-table-container::-webkit-scrollbar-track {
                    background: transparent;
                }
                .sidebar-content::-webkit-scrollbar-thumb,
                #manual-scroll-container::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 10px;
                }
                .sidebar-content::-webkit-scrollbar-thumb:hover,
                #manual-scroll-container::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }

                .toc-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .toc-item {
                    margin-bottom: 4px;
                }

                .toc-link {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 12px;
                    border-radius: 10px;
                    color: var(--text-muted);
                    text-decoration: none;
                    font-size: 0.9rem;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    position: relative;
                }

                .toc-link:hover {
                    color: var(--text-main);
                    background-color: rgba(241, 245, 249, 0.8);
                }

                .toc-link.active {
                    color: #fff;
                    background: linear-gradient(135deg, #6366f1, #4f46e5);
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
                    font-weight: 600;
                }

                .active-icon {
                    flex-shrink: 0;
                }

                /* Main Content Styles */
                .premium-content-area {
                    flex: 1;
                    overflow-y: auto;
                    scroll-behavior: smooth;
                    position: relative;
                }

                .content-container {
                    max-width: 960px;
                    margin: 0 auto;
                    padding: 40px 24px 80px;
                }

                .animate-fade-in {
                    animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Markdown Typography & Elements */
                .premium-markdown {
                    color: var(--text-main);
                    line-height: 1.7;
                }

                .md-h1 {
                    font-family: 'Outfit', sans-serif;
                    font-size: 2.75rem;
                    font-weight: 800;
                    letter-spacing: -0.025em;
                    background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    border-bottom: 2px solid rgba(226, 232, 240, 0.5);
                    padding-bottom: 0.5rem;
                }

                .md-h2 {
                    font-family: 'Outfit', sans-serif;
                    font-size: 2rem;
                    font-weight: 700;
                    letter-spacing: -0.02em;
                    color: #1e293b;
                }

                .md-h3 {
                    font-family: 'Outfit', sans-serif;
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #334155;
                }
                
                .md-h4 {
                    font-family: 'Outfit', sans-serif;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #475569;
                }

                .md-p {
                    font-size: 1.05rem;
                    color: #334155;
                }

                .md-ul, .md-ol {
                    font-size: 1.05rem;
                    color: #334155;
                    padding-left: 1.5rem;
                }

                .md-li::marker {
                    color: #8b5cf6;
                }

                .md-link {
                    color: #4f46e5;
                    text-decoration: none;
                    border-bottom: 1px solid transparent;
                    transition: border-color 0.2s;
                }

                .md-link:hover {
                    border-bottom-color: #4f46e5;
                }

                /* Code Blocks */
                .premium-code-block {
                    background: #0f172a;
                    border-radius: 12px;
                    box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2);
                    overflow: hidden;
                }

                .code-header {
                    background: #1e293b;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #334155;
                }

                .mac-dots {
                    display: flex;
                    gap: 6px;
                }

                .dot {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                }
                .dot.red { background: #ff5f56; }
                .dot.yellow { background: #ffbd2e; }
                .dot.green { background: #27c93f; }

                .code-lang {
                    color: #94a3b8;
                    font-size: 0.75rem;
                    font-family: 'Fira Code', monospace;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                .code-body {
                    padding: 20px;
                    overflow-x: auto;
                }

                .code-body code {
                    color: #e2e8f0;
                    font-family: 'Fira Code', monospace;
                    font-size: 0.9rem;
                    line-height: 1.6;
                }

                .code-body::-webkit-scrollbar-thumb {
                    background: #475569;
                }

                .inline-code {
                    background: #f1f5f9;
                    color: #ef4444;
                    padding: 3px 6px;
                    border-radius: 6px;
                    font-family: 'Fira Code', monospace;
                    font-size: 0.85em;
                    font-weight: 500;
                    border: 1px solid #e2e8f0;
                }

                /* Images */
                .premium-image-container {
                    display: flex;
                    justify-content: center;
                    background: #fff;
                    padding: 24px;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px -10px rgba(0,0,0,0.08);
                    border: 1px solid rgba(255,255,255,0.5);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .premium-image-container:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.12);
                }

                .premium-image {
                    max-width: 100%;
                    max-height: 600px;
                    border-radius: 12px;
                    object-fit: contain;
                }

                /* Tables */
                .premium-table-container {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
                    overflow: auto;
                    border: 1px solid #f1f5f9;
                }

                .premium-table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .premium-table th {
                    background: #f8fafc;
                    padding: 16px 20px;
                    text-align: left;
                    font-weight: 600;
                    color: #475569;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    border-bottom: 2px solid #e2e8f0;
                }

                .premium-table td {
                    padding: 16px 20px;
                    border-bottom: 1px solid #f1f5f9;
                    color: #334155;
                    font-size: 0.95rem;
                }

                .premium-table tr:last-child td {
                    border-bottom: none;
                }

                .premium-table tr:hover td {
                    background: #f8fafc;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .premium-sidebar {
                        position: fixed;
                        transform: translateX(-100%);
                    }
                    .premium-sidebar.open {
                        transform: translateX(0);
                    }
                    .content-container {
                        padding: 30px 16px 80px;
                    }
                    .md-h1 { font-size: 2.25rem; }
                    .md-h2 { font-size: 1.75rem; }
                }
            `}</style>
        </div>
    );
};

export default WebManualPage;
